import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import FocusLock from 'react-focus-lock';
import { isNil } from 'lodash';
import { Button } from 'antd';
import cx from 'classnames';

import {
  getAccountSettingFeatureList,
  getDashboardFeatureList,
  getSidebarFeatureList,
} from 'utils/onboardingUtils';
import {
  activeTourStepSelector,
  isTourStepActiveSelector,
  isWelcomeTourRunningSelector,
} from 'redux/selectors/onboardingSelectors';
import TourStep from './TourStep';
import TourMask from './TourMask';
import TourPortal from './TourPortal';
import { TourStepEnum } from '../enums';
import TourProgress from './TourProgress';
import TourCompletedModal from './TourCompletedModal';
import { finishWelcomeTour } from 'redux/reducers/onboardingSlice';
import { isRecruiterCompanySelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './WelcomeTour.module.less';

const getTourSteps = (isRecruiterCompany?: boolean) => ({
  [TourStepEnum.AccountSettings]: (
    <TourStep
      stepType={TourStepEnum.AccountSettings}
      className={styles.accountStep}
      title="Your Account Settings and Notifications are here"
      description={
        <>
          <p className={styles.tourSubtitle}>
            This section lets you manage your notifications and profile:
          </p>
          <ul className={styles.tourList}>
            {getAccountSettingFeatureList().map((featureName, index) => (
              <li key={`${featureName}-${index}`}>- {featureName}</li>
            ))}
          </ul>
        </>
      }
    />
  ),
  [TourStepEnum.Sidebar]: (
    <TourStep
      stepType={TourStepEnum.Sidebar}
      className={styles.sideBarStep}
      title="Left sidebar menu"
      description={
        <>
          <p className={styles.tourSubtitle}>This section lets you:</p>
          <ul className={styles.tourList}>
            {getSidebarFeatureList(isRecruiterCompany).map(
              (featureName, index) => (
                <li key={`${featureName}-${index}`}>- {featureName}</li>
              ),
            )}
          </ul>
        </>
      }
    />
  ),
  [TourStepEnum.HomeDashboard]: (
    <TourStep
      stepType={TourStepEnum.HomeDashboard}
      className={styles.homeDshStep}
      title="Home"
      description={
        <>
          <p className={styles.tourSubtitle}>
            This is your personal dashboard, complete outlook of your company
            performance:
          </p>
          <ul className={styles.tourList}>
            {getDashboardFeatureList(isRecruiterCompany).map(
              (featureName, index) => (
                <li key={`${featureName}-${index}`}>- {featureName}</li>
              ),
            )}
          </ul>
        </>
      }
    />
  ),
  [TourStepEnum.Congratulations]: <TourCompletedModal />,
});

export default function WelcomeTour() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isRecruiterCompany = useAppSelector(isRecruiterCompanySelector);
  const activeTourStep = useAppSelector(activeTourStepSelector);
  const isCongratulationsStepActive = useAppSelector(
    isTourStepActiveSelector(TourStepEnum.Congratulations),
  );
  const isWelcomeTourRunning = useAppSelector(isWelcomeTourRunningSelector);
  const tourSteps = useMemo(() => getTourSteps(isRecruiterCompany), [
    isRecruiterCompany,
  ]);

  if (!isWelcomeTourRunning) return null;

  const handleSkipTour = () => {
    dispatch(finishWelcomeTour());
    navigate('/company-profile');
  };

  return (
    <TourPortal>
      <TourMask />

      <FocusLock>
        {!isNil(activeTourStep) && <>{tourSteps[activeTourStep]}</>}

        {!isCongratulationsStepActive && (
          <div className={styles.progressWrapper}>
            <TourProgress />

            <Button
              type="ghost"
              onClick={handleSkipTour}
              className={cx('borderless-btn', styles.skipButton)}
            >
              Skip
            </Button>
          </div>
        )}
      </FocusLock>
    </TourPortal>
  );
}
